import { defaultAskPunttNotificationPrefs } from "@mg/schemas/src/commons/askPunttNotificationPrefs";
import { useQueryClient } from "@tanstack/react-query";
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/AskV2ReviewEmailNotificationBanner.test.tsx
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  mergeReviewEmailNotificationPrefEnabled,
  shouldShowAskV2ReviewEmailNotificationCta,
} from "./reviewEmailNotificationCta";

import { useUI } from "../../contexts/ui";
import { updateCurrentUserProfile } from "../../services/userProfile";
import { useUiPrefs } from "../../utils/askPunttUiPrefs";
import { useCurrentUserProfile } from "../../utils/queries/userProfile";

export function useAskV2ReviewEmailNotificationCta(
  chatId: string | undefined,
  reviewInProgress: boolean,
) {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const queryClient = useQueryClient();
  const profile = useCurrentUserProfile();
  const { prefs, setPref } = useUiPrefs();
  const [enabling, setEnabling] = useState(false);

  // The profile response reports only the toggles the person actually set, so
  // an absent key resolves to its schema default — NOT to `false`, which is
  // what would otherwise offer the CTA to someone who is already opted in.
  const emailReviewComplete =
    profile.data?.askPunttNotificationPrefs?.emailReviewComplete ??
    defaultAskPunttNotificationPrefs.emailReviewComplete;
  const dismissedForChat = Boolean(
    chatId && prefs.dismissedReviewEmailNotificationCtaByChat?.[chatId],
  );

  const visible = shouldShowAskV2ReviewEmailNotificationCta({
    chatId,
    emailReviewComplete: profile.isSuccess ? emailReviewComplete : undefined,
    dismissedForChat,
    profileLoaded: profile.isSuccess,
    reviewInProgress,
  });

  const dismiss = useCallback(() => {
    if (!chatId) return;
    const previous = prefs.dismissedReviewEmailNotificationCtaByChat ?? {};
    setPref("dismissedReviewEmailNotificationCtaByChat", {
      ...previous,
      [chatId]: true,
    });
  }, [chatId, prefs.dismissedReviewEmailNotificationCtaByChat, setPref]);

  const enable = useCallback(async () => {
    if (!visible || enabling) return;
    setEnabling(true);
    try {
      await updateCurrentUserProfile({
        askPunttNotificationPrefs: mergeReviewEmailNotificationPrefEnabled(
          profile.data?.askPunttNotificationPrefs,
        ),
      });
      await queryClient.invalidateQueries({ queryKey: ["user-profile"] });
    } catch {
      notify({
        variant: "error",
        title: t("askV2.transport.reviewEmailNotificationEnableFailed"),
      });
    } finally {
      setEnabling(false);
    }
  }, [
    enabling,
    notify,
    profile.data?.askPunttNotificationPrefs,
    queryClient,
    t,
    visible,
  ]);

  return { visible, enabling, enable, dismiss };
}
